import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User as AppUser } from '@flows.cash/types'
import { Platform } from 'react-native'
import { useProfileStore } from './useProfileStore'
import { useCategoriesStore } from './useCategoriesStore'
import { useCurrencyStore } from './useCurrencyStore'
import * as authService from '../services/supabase/auth'
import { supabase } from '../services/supabase/client'
import { User as SupabaseUser } from '@supabase/supabase-js'
import { clearStoreData } from '../utils/storeUtils'

// Helper function to clear all data stores
const clearAllDataStores = () => {
  try {
    // Import stores dynamically to avoid circular dependencies
    const { useAccountsStore } = require('./useAccountsStore')
    const { useTransactionStore } = require('./useTransactionStore')
    
    // Use the utility function to clear all stores
    clearStoreData({
      profileStore: useProfileStore.getState(),
      categoriesStore: useCategoriesStore.getState(),
      currencyStore: useCurrencyStore.getState(),
      accountsStore: useAccountsStore.getState(),
      transactionStore: useTransactionStore.getState(),
    })
    
    console.log('All data stores cleared successfully')
  } catch (error) {
    console.error('Error clearing data stores:', error)
    throw error
  }
}

// Helper to get storage based on platform
const getStorage = async () => {
  if (Platform.OS !== 'web') {
    try {
      const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default
      return AsyncStorage
    } catch (error) {
      console.error('Failed to import AsyncStorage:', error)
      throw error
    }
  }
  return null
}

// Convert Supabase User to App User
const convertUser = (supabaseUser: SupabaseUser): AppUser => {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    displayName: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || '',
    emailVerified: supabaseUser.email_confirmed_at !== null,
    createdAt: supabaseUser.created_at || new Date().toISOString(),
    updatedAt: supabaseUser.updated_at || new Date().toISOString(),
    photoURL: supabaseUser.user_metadata?.avatar_url || undefined,
    phoneNumber: supabaseUser.phone || undefined
  };
};

export interface AuthState {
  user: AppUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isLoggingOut: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: () => Promise<void>
  loginWithApple: (identityToken?: string, nonce?: string) => Promise<void>
  register: (email: string, password: string, displayName: string) => Promise<void>
  logout: (isUserSwitching?: boolean) => Promise<void>
  resetPassword: (email: string) => Promise<void>
  clearError: () => void
  reset: () => void
  initializeAuthListener: () => (() => void)
}

// Add this static variable to track initialization status
let isAuthListenerInitialized = false;

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => {
      // Set initial state with loading true
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: true,
        isLoggingOut: false,
        error: null
      });
      
      // Set up auth state listener
      let lastLoggedInUserId: string | null = null;
      let unsubscribe: (() => void) | null = null;
      
      if (Platform.OS === 'web') {
        console.log('Core auth store - setting up auth listener');
        // Set up Supabase auth state listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('Core auth store - auth state changed:', event);
            
            if (event === 'SIGNED_IN' && session?.user) {
              const appUser = convertUser(session.user);
              console.log('Core auth store - user signed in:', appUser.id);
              
              // Check if this is a different user than before
              const isUserSwitching = lastLoggedInUserId !== null && lastLoggedInUserId !== appUser.id;
              lastLoggedInUserId = appUser.id;
              
              if (isUserSwitching) {
                console.log('User switching detected in auth listener, clearing previous user data');
                clearAllDataStores();
                
                // Also clear persistent storage on web when switching users
                if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
                  try {
                    localStorage.removeItem('flows-accounts-storage');
                    localStorage.removeItem('flows-transactions-storage');
                    localStorage.removeItem('flows-categories-storage');
                    localStorage.removeItem('flows-currency-storage');
                    localStorage.removeItem('flows-profile-storage');
                    localStorage.removeItem('auth-storage'); // Clear auth persistence during user switch
                    console.log('Cleared persistent storage items during user switch including auth');
                  } catch (error) {
                    console.warn('Error clearing localStorage during user switch:', error);
                  }
                }
              }
              
              set({
                user: appUser,
                token: session.access_token,
                isAuthenticated: true,
                isLoading: false,
                error: null
              });
              
              // Initialize profile if needed
              const profileStore = useProfileStore.getState();
              if (!profileStore.settings) {
                try {
                  await profileStore.fetchSettings(appUser.id);
                } catch (error) {
                  console.error('Error fetching settings in auth listener:', error);
                }
              }
            } else if (event === 'SIGNED_OUT') {
              console.log('Core auth store - user signed out');
              
              // Clear all data stores on sign out but NOT localStorage here
              // localStorage clearing should only happen during explicit logout or user switching
              clearAllDataStores();
              
              set({
                user: null,
                token: null,
                isAuthenticated: false,
                isLoading: false,
                error: null
              });
            } else {
              // For other events, just ensure loading is set to false
              set(state => ({
                ...state,
                isLoading: false
              }));
            }
          }
        );
        
        // Store the unsubscribe function
        unsubscribe = () => {
          subscription.unsubscribe();
        };
        
        // Check initial session
        supabase.auth.getSession().then(({ data }) => {
          console.log('Core auth store - initial session check:', !!data.session);
          
          if (data.session?.user) {
            const appUser = convertUser(data.session.user);
            lastLoggedInUserId = appUser.id;
            
            set({
              user: appUser,
              token: data.session.access_token,
              isAuthenticated: true,
              isLoading: false,
              error: null
            });
            
            // Initialize profile if needed
            const profileStore = useProfileStore.getState();
            if (!profileStore.settings) {
              profileStore.fetchSettings(appUser.id).catch(error => {
                console.error('Error fetching settings in initial session check:', error);
              });
            }
          } else {
            set({
              isLoading: false
            });
          }
        }).catch(error => {
          console.error('Error checking initial session:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to check session'
          });
        });
      }
      
      const storeState: AuthState = {
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        isLoggingOut: false,
        error: null,
        
        async login(email: string, password: string) {
          try {
            set({ isLoading: true, error: null })
            
            const response = await authService.signInWithEmailAndPassword(email, password)
            
            if (response.error) {
              throw new Error(response.error.message)
            }
            
            // Auth state listener will handle setting the user
            set({ isLoading: false })
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to login'
            set({
              error: errorMessage,
              isLoading: false,
            })
            throw new Error(errorMessage)
          }
        },
        
        async loginWithGoogle() {
          try {
            set({ isLoading: true, error: null })
            
            const response = await authService.signInWithGoogle()
            
            if (response.error) {
              throw new Error(response.error.message)
            }
            
            // Auth state listener will handle setting the user
            set({ isLoading: false })
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to login with Google'
            set({
              error: errorMessage,
              isLoading: false,
            })
            throw new Error(errorMessage)
          }
        },
        
        async loginWithApple(identityToken?: string, nonce?: string) {
          try {
            set({ isLoading: true, error: null })
            
            let response;
            
            // Check if we're on mobile and have an identity token (mobile native flow)
            if (Platform.OS !== 'web' && identityToken) {
              console.log('Using mobile Apple Sign-In with identity token')
              const { appleAuthService } = await import('../services/appleAuthService')
              response = await appleAuthService.signInWithApple(identityToken, nonce)
              
              // For mobile, we get the session directly
              if (response.session?.user) {
                const appUser = convertUser(response.session.user)
                set({
                  user: appUser,
                  token: response.session.access_token,
                  isAuthenticated: true,
                  isLoading: false,
                  error: null
                })
              } else {
                throw new Error('No session returned from Apple authentication')
              }
            } else {
              // Use web OAuth flow
              console.log('Using web Apple Sign-In OAuth flow')
              const webResponse = await authService.signInWithApple()
              
              if (webResponse.error) {
                throw new Error(webResponse.error.message)
              }
              
              // Auth state listener will handle setting the user for web OAuth
              set({ isLoading: false })
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to login with Apple'
            set({
              error: errorMessage,
              isLoading: false,
            })
            throw new Error(errorMessage)
          }
        },
        
        async register(email: string, password: string, displayName: string) {
          try {
            set({ isLoading: true, error: null })
            
            // For mobile signups, we need to redirect to the mobile app
            let emailRedirectTo: string | undefined = undefined;
            
            if (Platform.OS !== 'web') {
              // For mobile, use the custom app scheme to open directly in the mobile app
              emailRedirectTo = 'flowscash://auth/callback';
            } else {
              // For web, use the web domain
              emailRedirectTo = `${window.location.origin}/auth/callback`;
            }
            
            console.log('🔗 Registration emailRedirectTo:', emailRedirectTo);
            console.log('📱 Platform.OS:', Platform.OS);
            console.log('🚀 __DEV__:', __DEV__);
            
            const { data, error } = await supabase.auth.signUp({
              email,
              password,
              options: {
                emailRedirectTo,
                data: {
                  display_name: displayName
                }
              }
            })
            
            console.log('📧 Supabase signUp response:', {
              user: data.user ? 'User created' : 'No user',
              session: data.session ? 'Session created' : 'No session (email confirmation required)',
              error: error ? error.message : 'No error'
            });
            
            if (error) {
              console.error('❌ Registration error:', error)
              throw error
            }
            
                         set({ 
               user: data.user ? convertUser(data.user) : null, 
               token: data.session?.access_token || null,
               isAuthenticated: !!data.session,
               isLoading: false 
             })
           } catch (error: any) {
             console.error('❌ Registration failed:', error)
             set({ 
               error: error.message || 'Registration failed', 
               isLoading: false 
             })
            throw error
          }
        },
        
        async logout(isUserSwitching = false) {
          try {
            set({ isLoggingOut: true, error: null })
            
            // Sign out from Supabase FIRST to prevent session restoration
            const response = await authService.signOut()
            
            if (response.error) {
              throw new Error(response.error.message)
            }
            
            // Clear auth state
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoggingOut: false,
              error: null
            })
            
            // Clear data stores and persistent storage AFTER auth state is cleared
            if (!isUserSwitching) {
              clearAllDataStores()
              
              // Also clear persistent storage on web, INCLUDING auth storage
              if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
                try {
                  localStorage.removeItem('flows-accounts-storage')
                  localStorage.removeItem('flows-transactions-storage')
                  localStorage.removeItem('flows-categories-storage')
                  localStorage.removeItem('flows-currency-storage')
                  localStorage.removeItem('flows-profile-storage')
                  localStorage.removeItem('auth-storage') // Clear auth persistence to prevent restoration
                  console.log('Cleared all persistent storage items including auth')
                } catch (error) {
                  console.warn('Error clearing localStorage:', error)
                }
              }
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to logout'
            set({
              error: errorMessage,
              isLoggingOut: false,
            })
            throw new Error(errorMessage)
          }
        },
        
        async resetPassword(email: string) {
          try {
            set({ isLoading: true, error: null })
            
            const response = await authService.resetPassword(email)
            
            if (response.error) {
              throw new Error(response.error.message)
            }
            
            set({ isLoading: false })
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to reset password'
            set({
              error: errorMessage,
              isLoading: false,
            })
            throw new Error(errorMessage)
          }
        },
        
        clearError() {
          set({ error: null })
        },
        
        reset() {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            isLoggingOut: false,
            error: null
          })
        },
        
        initializeAuthListener() {
          // Skip if already initialized
          if (isAuthListenerInitialized) {
            console.log('Auth listener already initialized, skipping duplicate initialization');
            return () => {}; // Return no-op function
          }
          
          console.log('Initializing auth listener');
          isAuthListenerInitialized = true;
          
          // Set up Supabase auth state listener
          const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
              console.log('Auth state changed:', event);
              
              if (event === 'SIGNED_IN' && session?.user) {
                const appUser = convertUser(session.user);
                console.log('User signed in:', appUser.id);
                
                // Check if this is a different user than before
                const isUserSwitching = lastLoggedInUserId !== null && lastLoggedInUserId !== appUser.id;
                lastLoggedInUserId = appUser.id;
                
                if (isUserSwitching) {
                  console.log('User switching detected in auth listener, clearing previous user data');
                  clearAllDataStores();
                  
                  // Also clear persistent storage on web when switching users
                  if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
                    try {
                      localStorage.removeItem('flows-accounts-storage');
                      localStorage.removeItem('flows-transactions-storage');
                      localStorage.removeItem('flows-categories-storage');
                      localStorage.removeItem('flows-currency-storage');
                      localStorage.removeItem('flows-profile-storage');
                      localStorage.removeItem('auth-storage'); // Clear auth persistence during user switch
                      console.log('Cleared persistent storage items during user switch in initializeAuthListener including auth');
                    } catch (error) {
                      console.warn('Error clearing localStorage during user switch in initializeAuthListener:', error);
                    }
                  }
                }
                
                set({
                  user: appUser,
                  token: session.access_token,
                  isAuthenticated: true,
                  isLoading: false,
                  error: null
                });
                
                // Initialize profile if needed
                const profileStore = useProfileStore.getState();
                if (!profileStore.settings) {
                  try {
                    await profileStore.fetchSettings(appUser.id);
                  } catch (error) {
                    console.error('Error fetching settings in auth listener:', error);
                  }
                }
              } else if (event === 'SIGNED_OUT') {
                console.log('User signed out');
                
                // Clear all data stores on sign out but NOT localStorage here
                // localStorage clearing should only happen during explicit logout or user switching
                clearAllDataStores();
                
                set({
                  user: null,
                  token: null,
                  isAuthenticated: false,
                  isLoading: false,
                  error: null
                });
              } else {
                // For other events, just ensure loading is set to false
                set(state => ({
                  ...state,
                  isLoading: false
                }));
              }
            }
          );
          
          // Check initial session
          supabase.auth.getSession().then(({ data }) => {
            console.log('Initial session check:', !!data.session);
            
            if (data.session?.user) {
              const appUser = convertUser(data.session.user);
              lastLoggedInUserId = appUser.id;
              
              set({
                user: appUser,
                token: data.session.access_token,
                isAuthenticated: true,
                isLoading: false,
                error: null
              });
              
              // Initialize profile if needed
              const profileStore = useProfileStore.getState();
              if (!profileStore.settings) {
                profileStore.fetchSettings(appUser.id).catch(error => {
                  console.error('Error fetching settings in initial session check:', error);
                });
              }
            } else {
              set({
                isLoading: false
              });
            }
          }).catch(error => {
            console.error('Error checking initial session:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to check session'
            });
          });
          
          // Return unsubscribe function that also resets the initialization flag
          return () => {
            subscription.unsubscribe();
            isAuthListenerInitialized = false;
          };
        }
      };
      
      // Add cleanup function if needed
      if (unsubscribe) {
        const originalReset = storeState.reset;
        storeState.reset = () => {
          if (unsubscribe) unsubscribe();
          originalReset();
        };
      }
      
      return storeState;
    },
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => ({
        async getItem(name: string) {
          try {
            if (Platform.OS === 'web') {
              const value = localStorage.getItem(name)
              return value ? value : null
            } else {
              const storage = await getStorage()
              if (storage) {
                const value = await storage.getItem(name)
                return value
              }
              return null
            }
          } catch (error) {
            console.error('Error getting stored auth data:', error)
            return null
          }
        },
        async setItem(name: string, value: any) {
          try {
            if (Platform.OS === 'web') {
              localStorage.setItem(name, JSON.stringify(value))
            } else {
              const storage = await getStorage()
              if (storage) {
                await storage.setItem(name, JSON.stringify(value))
              }
            }
          } catch (error) {
            console.error('Error storing auth data:', error)
          }
        },
        async removeItem(name: string) {
          try {
            if (Platform.OS === 'web') {
              localStorage.removeItem(name)
            } else {
              const storage = await getStorage()
              if (storage) {
                await storage.removeItem(name)
              }
            }
          } catch (error) {
            console.error('Error removing auth data:', error)
          }
        }
      })),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)
